import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { useLocale, useT } from '../i18n/locale-context';
import { CODE_LENGTH_KEY, isCompleteCode, resolveAuthError } from '../lib/auth-error';

/**
 * The e-mail step's own control, rendered INSIDE the strip row — and, since
 * 2026-10-06, under the honest alert status («Почта не подтверждена — придёт
 * одна подборка», `unconfirmed-email-notice.tsx`), so the one sentence that
 * names the problem carries the fix next to it. Lifted out of
 * `onboarding-banner.tsx` unchanged apart from the two props.
 *
 * Soft signup (ADR 2026-09-07) creates the account — and a session — on the
 * very first e-mail submit, with no code required, so `user.emailVerified`
 * stays `false` until the person opts to prove the address. Verification gates
 * OUTBOUND EMAIL ONLY (alerts, digests), never access.
 *
 * ## Why this is a step now and not a banner of its own
 *
 * It used to be `VerifyEmailNudge`: a second brand-tinted card stacked above
 * this strip, with its own heading, its own button and no relation to the
 * «Настройка профиля: 0 из 3» counter right underneath it. Two nags in the
 * same colour, one of them not counted by the other, at the top of the
 * catalogue on a mobile-first page. Folded into the checklist on 2026-09-21
 * (owner): one strip, one counter, four rows.
 *
 * The flow itself is unchanged — `POST /auth/otp/request`, six digits, `POST
 * /auth/otp/verify` — and it still runs in place rather than linking anywhere:
 * the code is only useful on the screen that asked for it.
 *
 * Dismisses itself the moment verification lands: `verifyOtp` calls the auth
 * context's `refresh()`, `user.emailVerified` flips, and the PARENT re-renders
 * with the row ticked. There is no local "done" state to fall out of sync.
 */
export function VerifyEmailAction({
  email,
  sendLabel,
  showWhy = true,
}: {
  email: string;
  /** The first button's label. The strip says «Отправить код»; the honest
   *  alert status (2026-10-06) says «Подтвердить почту», next to the sentence
   *  that explains why. */
  sendLabel?: string;
  /** The strip's one-line reason under the control. Off where the caller
   *  already printed its own reason. */
  showWhy?: boolean;
}) {
  const { requestOtp, verifyOtp } = useAuth();
  const { locale } = useLocale();
  const t = useT();
  const [stage, setStage] = useState<'idle' | 'code'>('idle');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSend() {
    setError(null);
    setBusy(true);
    try {
      await requestOtp({ email, locale });
      setStage('code');
    } catch (err) {
      setError(resolveAuthError(err, t));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    /*
     * Short code → say so here, do not spend a request finding out. The server
     * answers 400 `validation_error{field:'code'}`, which resolves to the SAME
     * sentence (`CODE_LENGTH_KEY`), so the pre-flight only removes a round trip
     * and a spinner — it never contradicts the server. Measured 2026-09-21: a
     * person pressed «Подтвердить» on a short code and was told «Что-то пошло
     * не так», which blames us for their typo.
     *
     * The button stays enabled (TZ §1) — a disabled one eats the tap in silence
     * and the person is left guessing which of the two controls is broken.
     */
    if (!isCompleteCode(code)) {
      setError(t(CODE_LENGTH_KEY));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await verifyOtp({ email, code: code.trim(), locale });
      // On success the context's `refresh()` flips `user.emailVerified` and the
      // parent stops rendering this. Nothing to clean up here.
    } catch (err) {
      setError(resolveAuthError(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {stage === 'idle' ? (
        /* Same shape and position as «Указать» on the ИНН row — the strip
           offers exactly one control per row, pinned right. */
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={busy}
          className="ml-auto shrink-0 rounded-md border-[1.5px] border-brand px-2.5 py-1 text-[12px] font-bold text-brand transition-colors hover:bg-brand-dim disabled:pointer-events-none disabled:opacity-60"
        >
          {sendLabel ?? t('onboarding.verifyEmail.send')}
        </button>
      ) : (
        <form
          onSubmit={(e) => void handleVerify(e)}
          className="ml-auto flex shrink-0 items-center gap-1.5"
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-label={t('register.code')}
            className="h-7 w-[4.5rem] rounded-md border border-edge bg-bone-surface px-2 text-center text-[13px] font-semibold text-ink focus:border-brand focus:outline-none"
          />
          {/* Never disabled on a short code — a silently dead button ate taps
              elsewhere on the site (TZ §1); the server rejects a bad code and
              the message lands below. */}
          <button
            type="submit"
            disabled={busy}
            className="h-7 shrink-0 rounded-md bg-brand px-2.5 text-[12px] font-bold text-white transition-colors hover:bg-brand-mid disabled:pointer-events-none disabled:opacity-60"
          >
            {t('onboarding.verifyEmail.verify')}
          </button>
        </form>
      )}
      {/* Spain: once the code is mailed, say where it went and how long it lasts.
          Otherwise «Confirmar el email» turns into a bare six-character box with
          nothing saying what to type into it (QA walk 2026-10-20). UZ/KZ: as before. */}
      {stage === 'code' && locale === 'es' && (
        <p className="basis-full text-[12px] leading-snug text-ink-muted">
          {t('register.codeSentTo')} <b className="font-semibold text-ink">{email}</b>.{' '}
          {t('register.codeHint')}
        </p>
      )}
      {/* `basis-full` puts the reason on its own line inside the wrapping row —
          the label is three words and cannot carry it. */}
      {showWhy && (
        <p className="basis-full text-[12px] leading-snug text-ink-muted">
          {/* One line on a phone instead of three (2026-10-06): the reason
              that matters is that digests reach the inbox. */}
          <span className="sm:hidden">{t('onboarding.banner.emailWhyShort')}</span>
          <span className="hidden sm:inline">{t('onboarding.banner.emailWhy')}</span>
        </p>
      )}
      {error && (
        <p className="basis-full text-[12px] text-danger" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
